import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Key, Save } from 'lucide-react';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SettingsContainer = styled(motion.div)`
  width: 90vw;
  max-width: 600px;
  max-height: 80vh;
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  color: ${props => props.theme.colors.text};
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled(motion.button)`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.text};
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const InputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ToggleButton = styled(motion.button)`
  position: absolute;
  right: 12px;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const Button = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  background: ${props => props.variant === 'primary' ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? '#ffffff' : props.theme.colors.text};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.variant === 'primary' ? 'transparent' : props.theme.colors.border};

  &:hover {
    background: ${props => props.variant === 'primary' ? '#005a9e' : props.theme.colors.hover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 13px;
  margin: 8px 0 0 0;
  line-height: 1.4;
`;

const Link = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  background: ${props => props.status === 'connected' ? '#10b98120' : '#ef444420'};
  color: ${props => props.status === 'connected' ? '#10b981' : '#ef4444'};
  border: 1px solid ${props => props.status === 'connected' ? '#10b98140' : '#ef444440'};
`;

function APISettings({ isOpen, onClose }) {
  const [model, setModel] = useState('oneline-chat');
  const [outputLang, setOutputLang] = useState('ko');
  const [supabaseUrl, setSupabaseUrl] = useState('');
  const [supabaseKey, setSupabaseKey] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // 저장된 설정 불러오기
      const savedModel = localStorage.getItem('oneline_model') || 'oneline-chat';
      const savedLang = localStorage.getItem('oneline_output_lang') || 'ko';
      const savedSupabaseUrl = localStorage.getItem('supabase_url') || '';
      const savedSupabaseKey = localStorage.getItem('supabase_anon_key') || '';
      const savedAdminEmail = localStorage.getItem('cover_admin_email') || '';
      const accountRaw = localStorage.getItem('ai_agent_account');
      let currentEmail = '';
      try { currentEmail = accountRaw ? (JSON.parse(accountRaw).email || '') : ''; } catch { currentEmail = ''; }
      const adminFlag = !!(currentEmail && savedAdminEmail && currentEmail === savedAdminEmail);
      setModel(savedModel);
      setOutputLang(savedLang);
      setSupabaseUrl(savedSupabaseUrl);
      setSupabaseKey(savedSupabaseKey);
      setAdminEmail(savedAdminEmail);
      setIsAdmin(adminFlag);
    }
  }, [isOpen]);

  const handleSave = () => {
    localStorage.setItem('oneline_model', model);
    localStorage.setItem('oneline_output_lang', outputLang);
    if (isAdmin) {
      if (supabaseUrl) localStorage.setItem('supabase_url', supabaseUrl);
      if (supabaseKey) localStorage.setItem('supabase_anon_key', supabaseKey);
      if (adminEmail) localStorage.setItem('cover_admin_email', adminEmail);
    }
    
    // AI 서비스 설정 업데이트
    if (window.aiService) {
      window.aiService.updateConfig();
    }
    
    onClose();
  };


  const handleClearSettings = () => {
    setModel('oneline-chat');
    localStorage.removeItem('oneline_model');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <SettingsContainer
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <Title>
              <Key size={20} />
              AI 모드 및 언어 선택
            </Title>
            <CloseButton
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
            >
              <X size={18} />
            </CloseButton>
          </Header>

          <Content>
            <Section>
              <SectionTitle>AI 모델 설정</SectionTitle>
              
              <FormGroup>
                <Label>모델</Label>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: `1px solid ${'#'}{${''}}` }}
                >
                  <option value="oneline-chat">One-Line Standard (일반 모드)</option>
                  <option value="oneline-reasoner">One-Line Pro (논문 모드)</option>
                </select>
                <InfoText>
                  Pro 모드는 더 긴 출력과 전문적인 연구 보고서 및 논문 작성에 최적화되어 있습니다.<br/> 
                  Standard 모드는 일반적인 보고서 및 글 작성에 최적화되어 있습니다. 추가적으로 계획서, 공부 자료 등 다양한 문서 작성에 사용할 수 있습니다.<br/>
                </InfoText>
              </FormGroup>

              {isAdmin && <SectionTitle>Supabase 설정</SectionTitle>}
              {isAdmin && (<FormGroup>
                <Label>Supabase URL</Label>
                <Input
                  type="url"
                  value={supabaseUrl}
                  onChange={(e) => setSupabaseUrl(e.target.value)}
                  placeholder="https://xxxx.supabase.co"
                />
              </FormGroup>)}
              {isAdmin && (<FormGroup>
                <Label>Supabase anon key</Label>
                <Input
                  type="password"
                  value={supabaseKey}
                  onChange={(e) => setSupabaseKey(e.target.value)}
                  placeholder="eyJhbGciOi..."
                />
                <InfoText>설정 시 계정 데이터 동기화 및 관리자 목록 조회가 가능합니다.</InfoText>
              </FormGroup>)}

              {isAdmin && (<FormGroup>
                <Label>관리자 이메일</Label>
                <Input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@example.com"
                />
                <InfoText>일치하는 계정만 관리자 메뉴를 볼 수 있습니다.</InfoText>
              </FormGroup>)}

              <FormGroup>
                <Label>출력 언어</Label>
                <select
                  value={outputLang}
                  onChange={(e) => setOutputLang(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: `1px solid ${'#'}{${''}}` }}
                >
                  <option value="ko">한국어</option>
                  <option value="en">English</option>
                  <option value="zh">中文</option>
                  <option value="ja">日本語</option>
                </select>
                <InfoText>설정한 언어로 보고서가 출력됩니다. 질문은 해당 언어로 질문해주세요.</InfoText>
              </FormGroup>

              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSave}
                  variant="primary"
                >
                  <Save size={16} />
                  저장
                </Button>
              </div>
            </Section>
          </Content>
        </SettingsContainer>
      </Overlay>
    </AnimatePresence>
  );
}

export default APISettings;
